import { Box, Typography, useTheme } from "@outschool/backpack";
import { creditsPerLearnerPerMeeting } from "@outschool/business-rules";
import { Activity } from "@outschool/gql-backend-generated";
import {
  Trans,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import React from "react";

import { useEsaUserWithoutPaidMembership } from "../../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../../Chips/FreeForMembersChip";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import EstimatedValueLineItem from "../mainBody/desktopActivityCards/EstimatedValueLineItem";

export default function MobileActivityCredits({
  activity,
  displayFullPriceOption = true,
}: {
  activity: Pick<
    Activity,
    | "allows_recurring_payment"
    | "isClub"
    | "is_ongoing_weekly"
    | "duration_weeks"
    | "hasTeacherSchedule"
    | "price_cents"
    | "weekly_meetings"
    | "isFlexSchedule"
    | "size_max"
    | "isOneTime"
    | "isSelfPaced"
    | "priceCredits"
    | "perWeeklyCourseCredits"
    | "isIncludedWithMembership"
  >;
  displayFullPriceOption?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\MobileActivityCredits",
  );

  const theme = useTheme();
  const { makePerMeetingPriceCreditsText } = useLocalPriceDisplay();
  const flexSx = { flexDirection: "row", gap: 4, alignItems: "end" };
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  if (activity.hasTeacherSchedule) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <MobileSizeCoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="subtitle1">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ marginBottom: 1 }}>
            {" "}
            credits/session
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isOneTime) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <MobileSizeCoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="subtitle1">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ marginBottom: 1 }}>
            {" "}
            credits/class
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.isClub) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <MobileSizeCoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="subtitle1">
              {{ perMonthPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ marginBottom: 1 }}>
            {" "}
            credits/month
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.is_ongoing_weekly && activity.weekly_meetings === 1) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <MobileSizeCoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="subtitle1">
              {{ perMeetingPriceCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ marginBottom: 1 }}>
            {" "}
            credits/week
          </Typography>
        </Box>
      </Trans>
    );
  } else if (
    activity.is_ongoing_weekly &&
    (activity.weekly_meetings || 0) > 1
  ) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <MobileSizeCoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="subtitle1">
                {{ weeklyPrice: activity.priceCredits }}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ marginBottom: 1 }}>
              {" "}
              credits/week
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <MobileSizeCoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="caption">
                (
                {{
                  perMeetingPriceCredits:
                    makePerMeetingPriceCreditsText(activity),
                }}{" "}
                credits/class)
              </Typography>
            </Box>
          )}
        </Box>
      </Trans>
    );
  } else if (activity.isSelfPaced) {
    if (activity.isIncludedWithMembership && !isEsaUserWithoutPaidMembership) {
      return (
        <Box flex sx={{ flexDirection: "column", gap: 10 }}>
          <FreeForMembersChip />
          <Trans t={t}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  alignItems: "end",
                }}
              >
                <Box flex gap={4} alignItems="center">
                  <MobileSizeCoinIcon
                    fillColor={theme.palette.warning.main}
                    strokeColor={theme.palette.warning[700]}
                  />
                  <Typography variant="subtitle1">0 </Typography>
                </Box>
                <Typography variant="caption" sx={{ marginBottom: 1 }}>
                  credits{" "}
                </Typography>
              </Box>
              <EstimatedValueLineItem
                activity={activity}
                sx={{ marginLeft: "2rem", lineHeight: "1" }}
              />
            </Box>
          </Trans>
        </Box>
      );
    }
    if (activity.duration_weeks === 1) {
      return (
        <Trans t={t}>
          <Box flex sx={{ flexDirection: "column" }}>
            <Box flex sx={flexSx}>
              <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
                <MobileSizeCoinIcon
                  fillColor={theme.palette.warning.main}
                  strokeColor={theme.palette.warning[700]}
                />
                <Typography variant="subtitle1">
                  {{ perCourseCredits: activity.priceCredits }}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ marginBottom: 1 }}>
                {" "}
                credits for all content
              </Typography>
            </Box>
          </Box>
        </Trans>
      );
    }
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <MobileSizeCoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="subtitle1">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ marginBottom: 1 }}>
              {" "}
              credits/week
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Typography
              variant="caption"
              marginLeft="2rem"
              color="text.secondary"
            >
              ({{ perCourseCredits: activity.priceCredits }} for all content)
            </Typography>
          )}
        </Box>
      </Trans>
    );
  } else if (!activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={flexSx}>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <MobileSizeCoinIcon
              fillColor={theme.palette.warning.main}
              strokeColor={theme.palette.warning[700]}
            />
            <Typography variant="subtitle1">
              {{ perCourseCredits: activity.priceCredits }}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ marginBottom: 1 }}>
            {" "}
            for{" "}
            {{
              classes:
                (activity.duration_weeks || 0) *
                (activity.weekly_meetings || 0),
            }}{" "}
            classes
          </Typography>
        </Box>
      </Trans>
    );
  } else if (activity.allows_recurring_payment) {
    return (
      <Trans t={t}>
        <Box flex sx={{ flexDirection: "column" }}>
          <Box flex sx={flexSx}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <MobileSizeCoinIcon
                fillColor={theme.palette.warning.main}
                strokeColor={theme.palette.warning[700]}
              />
              <Typography variant="subtitle1">
                {{
                  perWeeklyCourseCredits: !!activity.weekly_meetings
                    ? creditsPerLearnerPerMeeting(activity) *
                      activity.weekly_meetings
                    : creditsPerLearnerPerMeeting(activity),
                }}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ marginBottom: 1 }}>
              {" "}
              credits/week
            </Typography>
          </Box>
          {displayFullPriceOption && (
            <Typography
              variant="caption"
              marginLeft="2rem"
              color="text.secondary"
            >
              ({{ perCourseCredits: activity.priceCredits }} for{" "}
              {{
                classes:
                  (activity.duration_weeks || 0) *
                  (activity.weekly_meetings || 0),
              }}{" "}
              classes)
            </Typography>
          )}
        </Box>
      </Trans>
    );
  } else {
    return null;
  }
}

const MobileSizeCoinIcon = ({
  fillColor,
  strokeColor,
}: {
  fillColor: string;
  strokeColor: string;
}) => {
  return (
    <CoinIcon
      fillColor={fillColor}
      strokeColor={strokeColor}
      width={16}
      height={16}
    />
  );
};
